import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { FiSend } from "react-icons/fi";
import CommentItem from "./CommentItem";
import CommentInput from "./CommentInput";

const token = Cookies.get("userToken");

const headers = {
	headers: {
		Authorization: `Bearer ${token}`,
	},
};

const CommentModal = ({
	comments = [],
	user,
	value,
	onChange,
	onClose,
	onSubmit,
	// fetchPosts,
	postId,
	setOptimisticComments,
	isSubmitting,
	setIsSubmitting
}) => {
	const [replyingTo, setReplyingTo] = useState(null);
	const [likingCommentIds, setLikingCommentIds] = useState(() => new Set());
	const [optimisticLikes, setOptimisticLikes] = useState({});
	const [optimisticLikeCounts, setOptimisticLikeCounts] = useState({});
	
	const breakLongWords = useCallback((text, maxLength = 15) => {
		return text
			.split(" ")
			.map(word =>
				word.length > maxLength
					? word.match(new RegExp(`.{1,${maxLength}}`, "g")).join("\u200B")
					: word
			)
			.join(" ");
	}, []);

	const likeComment = async (commentId) => {
		return axios.post(
			`${process.env.REACT_APP_BACKEND_URL}/apis/admin/likeComment/${commentId}`,
			{},
			headers
		);
	};

	const replyToComment = async (postId, commentId, replyText) => {
		return axios.post(
			`${process.env.REACT_APP_BACKEND_URL}/apis/admin/addReplyInComment/${postId}`,
			{
				text: replyText,
				parentCommentId: commentId,
			},
			headers
		);
	};

	const handleLike = useCallback(async (commentId) => {
		if (!commentId || commentId.startsWith("temp-")) return;

		// 🔥 INSTANT UI FEEDBACK (icon/color)
		setOptimisticLikes(prev => {
			const current = prev[commentId] ?? comments.find(c => c._id === commentId)?.isLiked;
			return {
				...prev,
				[commentId]: !current
			};
		});

		setOptimisticLikeCounts(prev => {
			const currentCount =
				prev[commentId] ??
				comments.find(c => c._id === commentId)?.likeCount ??
				0;

			const isLiking =
				!(optimisticLikes[commentId] ??
					comments.find(c => c._id === commentId)?.isLiked);

			return {
				...prev,
				[commentId]: isLiking
					? currentCount + 1
					: Math.max(0, currentCount - 1)
			};
		});

		setLikingCommentIds(prev => {
			const next = new Set(prev);
			next.add(commentId);
			return next;
		});

		try {
			const res = await likeComment(commentId);
			const updated = res.data.data;

			const serverLiked = updated.likes.some(
				l => l._id === user._id
			);

			setOptimisticLikeCounts(prev => {
				const next = { ...prev };
				delete next[commentId];
				return next;
			});

			setOptimisticLikes(prev => {
				const next = { ...prev };
				delete next[commentId];
				return next;
			});

			// 🔥 SERVER TRUTH (count + final state)
			setOptimisticComments(prev => {
				const arr = [...prev[postId]];
				const idx = arr.findIndex(c => c._id === commentId);
				if (idx !== -1) {
					arr[idx] = {
						...arr[idx],
						isLiked: serverLiked,
						likeCount: updated.likes.length
					};
				}
				return { ...prev, [postId]: arr };
			});

		} catch (e) {
			setOptimisticLikes(prev => {
				const next = { ...prev };
				delete next[commentId];
				return next;
			});

			setOptimisticLikeCounts(prev => {
				const next = { ...prev };
				delete next[commentId];
				return next;
			});
		} finally {
			setLikingCommentIds(prev => {
				const next = new Set(prev);
				next.delete(commentId);
				return next;
			});
		}
	}, [comments, optimisticLikes, setOptimisticComments, user._id, postId]);

	const handleReply = async () => {
		if (!replyingTo || !value.trim()) return;

		setIsSubmitting(true);

		// 🔥 instantly clear textarea
		onChange({ target: { value: "" } });

		const tempReplyId = `temp-reply-${Date.now()}`;
		const tempReply = {
			_id: tempReplyId,
			text: value.trim(),
			user: { _id: user._id, fullName: user.fullName },
			createdAt: new Date().toISOString(),
			isOptimistic: true
		};

		setOptimisticComments(prev => {
			const updated = [...(prev[postId] || [])];
			const idx = updated.findIndex(c => c._id === replyingTo._id);
			if (idx !== -1) {
				updated[idx] = {
					...updated[idx],
					replies: [...(updated[idx].replies || []), tempReply]
				};
			}
			return { ...prev, [postId]: updated };
		});

		try {
			await replyToComment(postId, replyingTo._id, value.trim());
		} catch (err) {
			console.error("Reply failed:", err);
		} finally {
			setIsSubmitting(false);
			setReplyingTo(null);
		}
	};

	useEffect(() => {
		const scrollbarWidth =
			window.innerWidth - document.documentElement.clientWidth;

		document.body.style.overflow = "hidden";
		document.body.style.paddingRight = `${scrollbarWidth}px`;

		return () => {
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
		};
	}, []);

	return (
		<div className="fixed inset-0 bg-stone-900/30 flex justify-center items-center z-50 p-2 sm:p-4 transition-all">
			<div className="bg-white w-full max-w-md sm:max-w-lg md:max-w-xl max-h-[90vh] min-h-[50vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-stone-200">

				{/* Header: Clean & Centered */}
				<div className="px-5 py-4 border-b border-stone-200 flex justify-between items-center bg-white/80 backdrop-blur-md sticky top-0 z-10">
					<div>
						<h2 className="text-lg font-bold text-stone-800">Comments</h2>
						<p className="text-xs text-stone-400 font-medium">{comments.length} items</p>
					</div>
					<button
						onClick={onClose}
						className="p-2 rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-800 transition-colors"
					>
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* Body: High Readability */}
				<div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 scrollbar-thin scrollbar-thumb-stone-200">
					{comments.length === 0 ? (
						<div className="flex flex-col items-center justify-center h-full py-10 opacity-60">
							<div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-3">
								<FiSend className="text-stone-400" size={24} />
							</div>
							<p className="text-stone-500 text-sm font-medium">No comments yet. Be the first!</p>
						</div>
					) : (
						comments.map((comment) => (
							<CommentItem
								key={comment._id}
								comment={comment}
								breakLongWords={breakLongWords}
								handleLike={handleLike}
								likingCommentIds={likingCommentIds}
								optimisticLikes={optimisticLikes}
								setReplyingTo={setReplyingTo}
								optimisticLikeCounts={optimisticLikeCounts}
							/>
						))
					)}
				</div>

				{/* Input Area: Interactive & Polished */}
				<CommentInput
					value={value}
					onChange={onChange}
					onSubmit={onSubmit}
					replyingTo={replyingTo}
					handleReply={handleReply}
					setReplyingTo={setReplyingTo}
					isSubmitting={isSubmitting}
				/>
			</div>
		</div>
	);
};

export default CommentModal;